import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/fp/isEmpty';

import { Loading } from '@autopass-web/mochi-component';

import { PATH } from '../../config';
import storage from '../../service/storage';
import { useBootStraps } from '../../services/use-bootstraps';
import { usePlusPayContextInit } from './use-plus-pay-context-init';

interface DenDenWrapperProps {
  children: React.ReactNode;
}

const ALLOWED_PATHS = [
  // onboarding
  PATH.USER_WELCOME,
  PATH.USER_ADD_PLATE_NUMBER,
  PATH.USER_ACCOUNT_BINDING,
  PATH.USER_BIND_CARD,
  // main
  PATH.MAIN,
  PATH.SERVICES,
  PATH.LICENSE_PLATES,
  PATH.LICENSE_PLATES_NEW,
  PATH.PAYMENT,
  PATH.RECEIPT_SETTING,
  PATH.RECEIPT_SETTING_DONATE,
  PATH.RECEIPT_SETTING_DOWNLOAD,
  PATH.PROMOTION,
  PATH.PROMOTION_DETAIL,
  PATH.PROMOTION_GROUP,
  PATH.CONTACT,
  PATH.TERMS,
  PATH.SETTINGS,
  // orders
  PATH.ORDERS,
  PATH.ORDERS_DETAILS,
  // maps
  PATH.MAPS,

  PATH.AUTH,
  PATH.EXPIRED,
];

const ON_BOARDING_PATHS = [
  PATH.USER_WELCOME,
  PATH.USER_ADD_PLATE_NUMBER,
  PATH.USER_ACCOUNT_BINDING,
  PATH.USER_BIND_CARD,
];

const AUTH_REQUIRED_PATHS = ALLOWED_PATHS.filter(
  (p) => !(p === PATH.AUTH || p === PATH.EXPIRED),
);

const checkIsAllowedPath = (path: string) =>
  ALLOWED_PATHS.some((p) => path.includes(p));

const checkIsOnBoardingPath = (path: string) =>
  ON_BOARDING_PATHS.some((p) => path.includes(p));

export const checkIsPlusPayAuthRequiredPath = (path: string) =>
  AUTH_REQUIRED_PATHS.some((p) => path.includes(p));

export const PlusPayWrapper = ({ children }: DenDenWrapperProps) => {
  useBootStraps();
  const [hasCheckRouting, setHasCheckRouting] = useState<boolean>(false);
  const { asPath, replace, push, query } = useRouter();
  const queryClient = useQueryClient();

  usePlusPayContextInit();

  useEffect(() => {
    const navigateToAuth = async () => {
      await push({
        pathname: PATH.AUTH,
        query: {
          ...query,
          prevPath: asPath,
          mainPath: PATH.MAIN,
        },
      });
      setHasCheckRouting(true);
    };

    async function checkPath() {
      if (hasCheckRouting) return;

      if (!checkIsAllowedPath(asPath)) {
        await replace(PATH.EXPIRED);
        setHasCheckRouting(true);
        return;
      }

      const oneTimeToken = query.OneTimeToken as string | undefined;

      // If oneTimeToken exists, redirect to auth page
      if (oneTimeToken) {
        await navigateToAuth();
        return;
      }

      const auth = storage.getAuth();

      if (!isEmpty(auth)) {
        const isFirstTimeLogin = auth.isFirstTimeLogin;

        if (!isFirstTimeLogin && checkIsOnBoardingPath(asPath)) {
          await replace(PATH.MAIN);
        } else {
          // FIXME: This is a weird workaround to fix the problem that the page gets 404 while first accessing the page
          await replace(asPath, asPath);
        }
        setHasCheckRouting(true);
        return;
      }

      // If the path is auth required, redirect to auth page
      if (checkIsPlusPayAuthRequiredPath(asPath)) {
        await navigateToAuth();
        return;
      }

      // FIXME: This is a weird workaround to fix the problem that the page gets 404 while first accessing the page
      replace(asPath);
      setHasCheckRouting(true);
    }

    checkPath();
  }, [asPath, push, replace, hasCheckRouting, query, queryClient]);

  if (!hasCheckRouting) {
    return <Loading />;
  }

  return children;
};
